import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import * as Sentry from "@sentry/react";
import { getSingleBooking, cancelBooking } from "../store/actions/bookingActions";
import { getVisionBookingByVisionId } from "../store/actions/visionAgentActions";
import PickupDropoffSection from "../components/SummaryScreenSections/PickupDropoffSection";
import CancelBookingModal from "../components/Booking/CancelBookingModal";
import { useToaster } from "../components/Toaster";
import LoadingMoveez from "../components/Lottie/LoadingMoveez";
import TripLoading from "../components/Booking/TripLoading";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBookingDate(dateStr, exactTime) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const monthDay = date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    const startTime = exactTime ? exactTime.split("-")[0].trim() : "";
    return `${dayName}, ${monthDay}${startTime ? ` | ${exactTime}` : ""}`;
}

function getStatusBadge(status) {
    if (!status) return { label: "Unknown", bg: "#E5E5F7", color: "#7876A1" };
    const s = status.toUpperCase();
    if (s === "CONFIRMED") return { label: "Confirmed", bg: "#D1FAE5", color: "#065F46" };
    if (s === "COMPLETED") return { label: "Completed", bg: "#DBEAFE", color: "#1E40AF" };
    if (s === "CANCELLED" || s === "CANCELED") return { label: "Cancelled", bg: "#FEE2E2", color: "#991B1B" };
    if (s === "INCOMPLETED") return { label: "Pending", bg: "#FEF3C7", color: "#92400E" };
    return { label: status, bg: "#E5E5F7", color: "#7876A1" };
}


const clearLocalStorage = () => {
    const localStorageRemove = [
        "VBO",
        "visionReqId",
        "bookingId",
        "VBItems",
        "VBSummary",
        "VBOLog"
    ]

    localStorageRemove.forEach((item) => localStorage.removeItem(item));
}

function getBookingStartDateTime(dateStr, exactTime) {
    if (!dateStr) return null;

    const normalizedExactTime = String(exactTime || "").trim();
    const rangeMatch = normalizedExactTime.match(/^(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)$/i);
    const startTime = rangeMatch ? rangeMatch[1].toUpperCase() : "";
    const parsed = new Date(`${dateStr}${startTime ? ` ${startTime}` : ""}`);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    const dateOnly = new Date(dateStr);
    return Number.isNaN(dateOnly.getTime()) ? null : dateOnly;
}

function getRelativeHoursFromExactTime(exactTime) {
    const value = String(exactTime || "").trim().toLowerCase();
    const match = value.match(/^within\s*(\d+(?:\.\d+)?)\s*hours?$/);
    if (!match) return null;

    const hours = Number(match[1]);
    return Number.isNaN(hours) ? null : hours;
}

function isWithinTwoHoursExactTime(exactTime) {
    const relativeHours = getRelativeHoursFromExactTime(exactTime);
    return relativeHours !== null && relativeHours <= 2;
}

function getBookingActionState(status, dateStr, exactTime) {
    const normalizedStatus = String(status || "").toLowerCase();
    const isInProgress = ["in progress", "in_progress", "inprogress"].includes(normalizedStatus);
    const isCompleted = normalizedStatus === "completed";
    const isCancelled = normalizedStatus === "cancelled" || normalizedStatus === "canceled";
    const isConfirmed = normalizedStatus === "confirmed" || normalizedStatus === "incompleted";

    const relativeHours = getRelativeHoursFromExactTime(exactTime);
    const startDateTime = relativeHours == null ? getBookingStartDateTime(dateStr, exactTime) : null;
    const hoursUntilStart =
        relativeHours != null
            ? relativeHours
            : startDateTime
                ? (startDateTime.getTime() - Date.now()) / (1000 * 60 * 60)
                : Infinity;
    const isConfirmedWithinOneHour = isConfirmed && hoursUntilStart >= 0 && hoursUntilStart <= 1;
    const isKnownSchedule = Number.isFinite(hoursUntilStart);
    const canDirectCancel = isConfirmed && isKnownSchedule && hoursUntilStart > 24;

    return {
        isConfirmed,
        isConfirmedWithinOneHour,
        canDirectCancel,
        showEditButton: isConfirmed,
        showCancelButton: isConfirmed,
        showBookAgainButton: isCompleted || isCancelled,
        showNoChangesMessage: isInProgress,
    };
}

// ─── Divider ─────────────────────────────────────────────────────────────────

const Divider = ({ mt = 18, mb = 20 }) => (
    <div
        style={{
            borderTop: "1px solid #7876A1",
            height: 0,
            marginTop: `${mt}px`,
            marginBottom: `${mb}px`,
        }}
    />
);

// ─── Main component ───────────────────────────────────────────────────────────

const SingleBookingDetails = () => {
    const [bookingObj, setbookingObj] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [modalConfig, setModalConfig] = useState(null);
    const [editBtnLoading, setEditBtnLoading] = useState(false);
    const [cancelBtnLoading, setCancelBtnLoading] = useState(false);

    const dispatch = useDispatch();
    const history = useNavigate();
    const { userId, bookingId } = useParams();
    const { bookingInfo } = useSelector((state) => state.bookingReducer);
    const { showToast } = useToaster();

    useEffect(() => {
        dispatch(getSingleBooking(bookingId));
    }, [bookingId, dispatch]);

    useEffect(() => {
        if (bookingInfo) {
            setbookingObj(bookingInfo);
            setInitialLoading(false);
        }
    }, [bookingInfo]);

    const handleEditBooking = async () => {
        if (!bookingObj?.visionRequestId) return;
        setEditBtnLoading(true);
        try {
            const response = await getVisionBookingByVisionId(bookingObj.visionRequestId);
            if (response.isSuccess === false) {
                Sentry.captureException(new Error("Failed to retrieve vision booking data"));
                return;
            }
            localStorage.setItem("VBO", JSON.stringify(response?.data));
            localStorage.setItem("visionReqId", bookingObj.visionRequestId);
            history("/?mode=edit");
        } catch (error) {
            console.error("Edit error:", error);
            showToast({
                type: "error",
                message: "An error occurred while trying to edit the booking.",
                duration: 4500,
            });
        } finally {
            setEditBtnLoading(false);
        }
    };

    if (initialLoading) {
        return (
       <TripLoading/>
        );
    }

    const statusBadge = getStatusBadge(bookingObj?.status);
    const actionState = getBookingActionState(bookingObj?.status, bookingObj?.deliveryDate, bookingObj?.exactTime);
    const dateDisplay = formatBookingDate(bookingObj?.deliveryDate, bookingObj?.exactTime);
    const vehicleName = bookingObj?.vehicle?.title || "";
    const labourNeeded = bookingObj?.labourNeeded || 0;
    const items = bookingObj?.items;
    const totalCharge = bookingObj?.totalCharge;

    const openEditWithinOneHourModal = () => {
        setModalConfig({
            title: "Call movers directly",
            description:
                "Your booking starts within 1 hour. Please call your movers directly to request any booking changes.",
            confirmText: "Got it",
            showSecondaryButton: false,
            onConfirm: () => setModalConfig(null),
        });
    };

    const openEditWithinTwoHourModal = () => {
        setModalConfig({
            title: "Call our team",
            description: (
                <>
                    You are trying to edit your booking within our 2-hour window. Changes at this stage may not be possible. Please call{' '}
                    <a href="tel:+18253055300" className="font-semibold underline" style={{ color: '#34318C' }}>
                        (825) 305-5300
                    </a>{' '}
                    to speak with our team.
                </>
            ),
            confirmText: "Got it",
            showSecondaryButton: false,
            onConfirm: () => setModalConfig(null),
        });
    };

    const openCancelPolicyModal = () => {
        const isWithinOneHour = actionState.isConfirmedWithinOneHour;
        const isDirectCancelAllowed = actionState.canDirectCancel;

        setModalConfig({
            title: "Cancellation notice",
            description: (
                <>
                    {isWithinOneHour
                        ? "Your booking is less than 1 hour away and cancellation fees are likely to apply. Please call "
                        : isDirectCancelAllowed
                            ? ""
                            : "You are trying to cancel your booking within our 24-hour cancellation window. Please call "}
                    <a href="tel:+18253055300" className="font-semibold underline" style={{ color: "#34318C" }}>
                        (825) 305-5300
                    </a>
                    {isWithinOneHour ? " immediately to confirm your cancellation." : " to confirm your cancellation."}
                </>
            ),
            confirmText: "Got it",
            showSecondaryButton: false,
            onConfirm: () => setModalConfig(null),
        });
    };

    const openCancelConfirmModal = () => {
        setModalConfig({
            title: "Cancel booking?",
            description:
                "Are you sure you want to cancel this booking? If your movers are already on the way, a cancellation fee may apply.",
            confirmText: cancelBtnLoading ? "Cancelling..." : "Cancel booking",
            secondaryText: "Keep booking",
            showSecondaryButton: true,
            onConfirm: async () => {
                if (cancelBtnLoading) return;
                setCancelBtnLoading(true);
                await cancelBooking(bookingId, userId, setCancelBtnLoading, history);
            },
        });
    };

    const handleEditBookingClick = () => {
        if (isWithinTwoHoursExactTime(bookingObj?.exactTime)) {
            openEditWithinTwoHourModal();
            return;
        }

        if (actionState.isConfirmedWithinOneHour) {
            openEditWithinOneHourModal();
            return;
        }
        handleEditBooking();
    };

    const handleCancelBookingClick = () => {
        if (actionState.canDirectCancel) {
            openCancelConfirmModal();
            return;
        }
        openCancelPolicyModal();
    };

    const handleBookAgainClick = () => {
        clearLocalStorage();
        history("/");
    };

    return (
        <div className="px-[33px] font-instrument pb-[10px] max-w-2xl mx-auto">

            {/* ── Title + status badge ── */}
            <div className="flex items-center gap-x-[10px] mb-[10px]">
                <button
                    type="button"
                    onClick={() => history(-1)}
                    aria-label="Go back"
                    className="w-8 h-8 rounded-full border border-[#D5D4E7] text-[#211F59] flex items-center justify-center hover:bg-[#F3F3FA] transition-colors"
                >
                    <i className="fas fa-chevron-left text-[12px]"></i>
                </button>
                <p className="text-[#211F59] text-[24px] font-semibold" style={{ lineHeight: "28px" }}>
                    Job details
                </p>
                <span
                    className="text-[12px] font-semibold px-[10px] py-[4px] rounded-full"
                    style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                >
                    {statusBadge.label}
                </span>
            </div>

            {/* ── Booking meta ── */}
            {dateDisplay && (
                <p className="text-[#211F59] inline text-[14px] font-medium mb-[4px]">{dateDisplay}</p>
            )}
            {(labourNeeded > 0 || vehicleName) && (
                <p className="text-[#211F59] text-[14px] mb-[4px]">
                    {labourNeeded > 0 ? `${labourNeeded} movers` : ""}
                    {labourNeeded > 0 && vehicleName ? " | " : ""}
                    {vehicleName}
                </p>
            )}
            {bookingObj?.id && (
                <p className="text-[#7876A1] text-[13px] mb-[18px]">Book #MVZ-{bookingObj.id}</p>
            )}

            <Divider mt={0} mb={20} />

            {/* ── Location section (shared component for consistent timeline math/colors) ── */}
            <p className="text-[#211F59] text-[16px] font-bold mb-[16px]">Location</p>
            <PickupDropoffSection
                pickUpPersonName={bookingObj?.pickUpPersonName}
                pickUpPersonPhone={bookingObj?.pickUpPersonPhone}
                pickupLocation={bookingObj?.pickupLocation}
                pickUpInstructions={bookingObj?.pickUpInstructions}
                dropOffPersonName={bookingObj?.dropOffPersonName}
                dropOffPersonPhone={bookingObj?.dropOffPersonPhone}
                dropoffLocation={bookingObj?.dropoffLocation}
                dropOffInstructions={bookingObj?.dropOffInstructions}
                stops={bookingObj?.stops || []}
                handleUpdateBookingObject={() => { }}
                showEditActions={false}
                showBottomDivider={false}
            />

            <Divider mt={18} mb={23} />

            {/* ── Crew & vehicle ── */}
            <div className="mb-[4px]">
                <p className="text-[#211F59] text-[16px] font-bold mb-[10px]">Crew & vehicle</p>
                <ul className="text-[#211F59] text-[14px] font-semibold list-[square] pl-[28px]">
                    {vehicleName && <li>{`1x ${vehicleName}`}</li>}
                    {labourNeeded > 0 && <li>{`${labourNeeded}x Movers`}</li>}
                </ul>
            </div>

            <Divider />

            {/* ── Items ── */}
            {Array.isArray(items) && items.length > 0 && (
                <>
                    <div className="mb-[4px]">
                        <p className="text-[#211F59] text-[16px] font-bold mb-[10px]">
                            Items 
                        </p>
                        <ul className="text-[#211F59] text-[14px] font-semibold list-[square] pl-[28px]">
                            {items.map((item, idx) => (
                                <li key={idx}>
                                    {item.quantity ? `${item.quantity}x ${item.name}` : item.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Divider />
                </>
            )}

            {/* ── Pricing ── */}
            <div className="mb-[4px]">
                <p className="text-[#211F59] text-[16px] font-bold mb-[14px]">Pricing</p>

                <div className="flex justify-between items-center">
                    <span className="text-[#7876A1] text-[14px]">Estimated total:</span>
                    <span className="text-[#211F59] text-[16px] font-bold">
                        {totalCharge ? `$${totalCharge}` : "—"}
                    </span>
                </div>
            </div>

            <Divider />

            {/* ── Support ── */}
            <div>
                <p className="text-[#211F59] text-[16px] font-bold mb-[14px]">Support</p>
                <button
                    onClick={()=> window.location.href = "https://book.moovez.ca/ContactUs"}
                    className="w-full h-[48px] flex items-center justify-center text-white text-[16px] font-semibold rounded-[8px] mb-[10px]"
                    style={{ backgroundColor: "#211F59" }}
                >
                    Contact support
                </button>
                {actionState.showNoChangesMessage ? (
                    <p className="text-[#7876A1] text-[14px] font-medium">This booking is in progress. Changes are no longer available.</p>
                ) : (
                    <div className="flex gap-[10px] mb-3">
                        {actionState.showEditButton && (
                            <button
                                className="flex-1 h-[48px] flex items-center justify-center text-[14px] bg-[#C8C6DF] text-[#34318C] font-semibold rounded-[8px] disabled:opacity-50"

                                disabled={editBtnLoading}
                                onClick={handleEditBookingClick}
                            >
                                {editBtnLoading ? "Loading…" : "Edit booking"}
                            </button>
                        )}

                        {/* Cancel Button  */}
                        {actionState.showCancelButton && (
                            <button
                                className="flex-1 h-[48px] flex items-center justify-center text-[14px] bg-[#FFBBB8] text-[#F11910] font-semibold rounded-[8px] disabled:opacity-50"

                                onClick={handleCancelBookingClick}
                                disabled={cancelBtnLoading}
                            >
                                {cancelBtnLoading ? "Cancelling..." : "Cancel"}
                            </button>
                        )}

                        {/* Book Again Button */}
                        {actionState.showBookAgainButton && (
                            <button
                                className="w-full h-[48px] text-[14px] flex items-center justify-center bg-[#F6F6F6] text-[14px] font-semibold rounded-[8px] text-[#211F59]"
                                style={{ border: "1.5px solid #211F59", color: "#211F59", backgroundColor: "#F0F0FA" }}
                                onClick={handleBookAgainClick}
                            >
                                Book again
                            </button>
                        )}
                    </div>
                )}
            </div>

            <CancelBookingModal
                isOpen={!!modalConfig}
                onClose={() => setModalConfig(null)}
                onConfirm={modalConfig?.onConfirm || (() => setModalConfig(null))}
                title={modalConfig?.title}
                description={modalConfig?.description}
                confirmText={modalConfig?.confirmText}
                secondaryText={modalConfig?.secondaryText}
                showSecondaryButton={modalConfig?.showSecondaryButton}
            />

        </div>
    );
};

export default SingleBookingDetails;
